<script setup lang="ts">
// ================================
// PRÉSENTATION — étape adresse bâtiment
// ================================
// Création du bâtiment via les services (équivalent de l’ancienne machine XState).

import {
    computed,
    inject,
    reactive,
    ref,
    toRaw,
    watch,
    type ComputedRef,
} from "vue"
import { useRoute, type RouteLocationNormalizedLoaded } from "vue-router"
import { useI18n } from "vue-i18n"
import type { Address, Building } from "@types"
import type { Services } from "@services"
import type { ParcelLocationMultiPolygon } from "@core/services/building.service"

// ================================
// TYPES
// ================================

interface ModelParcel {
    parcelId: string
    parcelGeometry: { type: string; coordinates?: unknown } | null
}

export interface FormBuildingAddressContext {
    name: string
    address: Address | null
    /** [longitude, latitude] (GeoJSON) */
    location: [number, number] | null
    level: string
    occupation: string | undefined
    modelParcel: ModelParcel
    skipParcel: boolean
    error: string
    building: Building | null
}

interface Emits {
    (e: "done:form", context: FormBuildingAddressContext): void
}

// ================================
// HELPERS
// ================================

/**
 * Première valeur string d’un query param Vue Router (évite string[] sur clés dupliquées).
 */
function getRouteQueryString(
    query: RouteLocationNormalizedLoaded["query"],
    key: string
): string {
    const v = query[key]
    if (v == null) return ""
    if (Array.isArray(v)) {
        const first = v[0]
        return typeof first === "string" ? first : ""
    }
    return typeof v === "string" ? v : ""
}

function verifyFormValidity(context: FormBuildingAddressContext): boolean {
    return (
        !!context.name &&
        !!context.address &&
        !!context.location &&
        !!context.level &&
        !!context.occupation &&
        (!!context.modelParcel?.parcelId || context.skipParcel)
    )
}

/**
 * Géométrie carte → MultiPolygon attendu par l’API (validation / création).
 */
function toParcelLocationPayload(
    geometry: unknown
): ParcelLocationMultiPolygon | undefined {
    if (!geometry || typeof geometry !== "object") return undefined
    const g = geometry as { type?: string; coordinates?: unknown }
    if (g.type === "MultiPolygon" && Array.isArray(g.coordinates)) {
        return {
            type: "MultiPolygon",
            coordinates: g.coordinates as number[][][][],
        }
    }
    if (g.type === "Polygon" && Array.isArray(g.coordinates)) {
        return {
            type: "MultiPolygon",
            coordinates: [g.coordinates as number[][][]],
        }
    }
    return undefined
}

// ================================
// SETUP
// ================================

const emit = defineEmits<Emits>()
const route = useRoute()
const services = inject<Services>("services")

const { t } = useI18n({
    messages: {
        fr: {
            title: "Ajouter un bâtiment",
            headingName: "Quel nom voulez-vous donner à ce bâtiment ?",
            headingAddress: "Où se situe ce bâtiment ?",
            headingOccupation: "Quel est le type de bâtiment ?",
            headingLevel: "Quel est le niveau de ce bâtiment ?",
            headingParcel:
                "Où se situe la parcelle cadastrale de ce bâtiment ?",
            namePlaceholder: "Ex. Maison principale",
            nameHint: "Donnez un nom pour identifier ce bâtiment",
            submit: "Valider",
        },
    },
})

type AddressStepState = "idle" | "loading" | "done" | "error"

const stateValue = ref<AddressStepState>("idle")

const context = reactive<FormBuildingAddressContext>({
    name: "",
    address: null,
    location: null,
    level: "",
    occupation: undefined,
    modelParcel: { parcelId: "", parcelGeometry: null },
    skipParcel: false,
    error: "",
    building: null,
})

const getStateValue = computed(() => stateValue.value)
const getContext = computed(() => context)

const getRouteQueryBuildingParentLevel = computed(() =>
    getRouteQueryString(route.query, "buildingParentLevel")
)
const getRouteQueryBuildingChildLevel = computed(() =>
    getRouteQueryString(route.query, "buildingChildLevel")
)

const getFormValid: ComputedRef<boolean> = computed(() =>
    verifyFormValidity(context)
)

function onInputName(value: string): void {
    context.name = value
}

function onInputAddress(value: Address | null): void {
    context.address = value
}

function onInputLocation(value: [number, number] | null): void {
    context.location = value
}

function onInputOccupation(value: string): void {
    context.occupation = value
}

function onInputLevel(value: string): void {
    context.level = value
    if (value === "groupe") {
        context.skipParcel = true
        context.modelParcel = { parcelId: "", parcelGeometry: null }
    }
}

function onInputParcel(value: ModelParcel): void {
    context.modelParcel = value
}

function onInputSkipParcel(value: boolean): void {
    context.skipParcel = value
}

async function runCreateBuilding(): Promise<void> {
    if (!services) {
        context.error = "Services non disponibles"
        stateValue.value = "error"
        return
    }
    if (
        !context.name ||
        !context.address ||
        !context.location ||
        !verifyFormValidity(context)
    ) {
        context.error = "Données formulaire incomplètes"
        stateValue.value = "error"
        return
    }

    try {
        const idBuilding = context.modelParcel?.parcelId?.trim() || undefined
        const parcelLocation = toParcelLocationPayload(
            context.modelParcel?.parcelGeometry
        )
        const building = await services.$createBuilding(
            context.name,
            context.address,
            {
                occupation: context.occupation ?? "residentiel",
                level: context.level,
                _id_folder: (route.query.folderId as string) || undefined,
                id_building: idBuilding,
                ...(idBuilding && parcelLocation
                    ? { parcel_location: parcelLocation }
                    : {}),
            }
        )

        const buildingParent = route.query.buildingParent as string | undefined
        const buildingChild = route.query.buildingChild as string | undefined

        if (buildingParent && building?._id) {
            await services.$addBuildingChildren(buildingParent, [building._id])
        }
        if (buildingChild && building?._id) {
            await services.$addBuildingChildren(building._id, [buildingChild])
        }

        context.building = building
        stateValue.value = "done"
    } catch (err) {
        context.error =
            err instanceof Error ? err.message : "Erreur lors de la création"
        stateValue.value = "error"
    }
}

function onSubmit(): void {
    if (!verifyFormValidity(context)) return
    context.error = ""
    stateValue.value = "loading"
    void runCreateBuilding()
}

watch(
    () => stateValue.value,
    (state) => {
        if (state === "done") {
            emit("done:form", { ...toRaw(context) })
        }
    }
)
</script>

<template>
    <div
        v-if="getStateValue !== 'done'"
        class="building-append-form flex flex-col items-center gap-8 w-full max-w-2xl"
    >
        <div class="building-append-form__section w-full">
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingName") }}
            </h3>
            <BaseInputText
                :model-value="getContext.name"
                class="w-full"
                :readonly="getStateValue === 'loading'"
                :placeholder="t('namePlaceholder')"
                :hint="t('nameHint')"
                @update:model-value="onInputName"
            />
        </div>

        <div class="building-append-form__section w-full">
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingAddress") }}
            </h3>
            <InputAddress
                :model-value="getContext.address"
                class="w-full"
                :readonly="getStateValue === 'loading'"
                data-cy="address-input"
                @update:model-value="onInputAddress"
                @update:coordinates="onInputLocation"
            />
        </div>

        <div
            class="building-append-form__section building-append-form__level w-full"
        >
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingOccupation") }}
            </h3>
            <InputBuildingOccupation
                :model-value="getContext.occupation"
                @update:model-value="onInputOccupation"
            />
        </div>
        <div
            class="building-append-form__section building-append-form__level w-full"
        >
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingLevel") }}
            </h3>
            <BuildingFormBoxListLevel
                class="mx-auto"
                :model-value="getContext.level"
                :parent="getRouteQueryBuildingParentLevel"
                :child="getRouteQueryBuildingChildLevel"
                :only="['groupe', 'immeuble', 'appartement']"
                @update:model-value="onInputLevel"
            />
        </div>

        <div
            v-if="
                getContext.address &&
                ['immeuble', 'appartement'].includes(getContext.level)
            "
            class="building-append-form__section building-append-form__map w-full"
        >
            <h3 class="text-base font-medium text-surface-800 mb-2">
                {{ t("headingParcel") }}
            </h3>
            <BuildingFormParcelMap
                :model-parcel="getContext.modelParcel"
                :model-skip-parcel="getContext.skipParcel"
                :readonly="getStateValue === 'loading'"
                :address-inline="getContext.address?.inline_name ?? ''"
                :address-location="getContext.location"
                disable-enedis-network
                @update:model-parcel="onInputParcel"
                @update:model-skip-parcel="onInputSkipParcel"
            />
        </div>

        <BaseButton
            type="submit"
            :label="t('submit')"
            :loading="getStateValue === 'loading'"
            :disabled="!getFormValid || getStateValue === 'loading'"
            @click="onSubmit"
        />

        <BaseMessage
            v-if="getStateValue === 'error'"
            class="building-append-form__error w-full"
            severity="error"
            :closable="false"
        >
            {{ getContext.error }}
        </BaseMessage>
    </div>
</template>
